import { useState, useEffect, useCallback, useRef } from 'react'
import { Card, CardHeader, CardTitle, CardContent, Button, Input, Badge } from '@/components/ui'
import { Mail, Copy, Check, RefreshCw, Trash2, Inbox, Clock, History } from 'lucide-react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'

const API_BASE = 'https://mails.scbphl.com/api'

interface Email {
  id: number
  message_id: string
  from: string
  sender_name: string
  subject: string
  body_text: string
  body_html: string
  date: string
  read: boolean
}

interface InboxEmail {
  id: number
  message_id: string
  sender: string
  sender_name: string
  subject: string
  received_at: string
  created_at: string
}

interface HistoryAddress {
  address: string
  created_at: string
}

interface StoredEmail {
  address: string
  subject: string
  from: string
  body: string
  date: string
  read: boolean
}

// Generate a persistent guest ID for unauthenticated users
const getGuestId = () => {
  let guestId = localStorage.getItem('tempmail_guest_id')
  if (!guestId) {
    guestId = 'guest_' + Math.random().toString(36).substring(2, 10)
    localStorage.setItem('tempmail_guest_id', guestId)
  }
  return guestId
}

// Get localStorage key for emails
const getEmailsKey = (userId: string) => `tempmail_emails_${userId}`
const getEmails = (userId: string): StoredEmail[] => {
  try {
    return JSON.parse(localStorage.getItem(getEmailsKey(userId)) || '[]')
  } catch {
    return []
  }
}

// Save emails to localStorage
const saveEmails = (userId: string, emails: StoredEmail[]) => {
  localStorage.setItem(getEmailsKey(userId), JSON.stringify(emails))
}

export function TempMail() {
  const { user } = useAuth()
  // Use user ID if logged in, otherwise use guest ID
  const userId = user?.id || getGuestId()
  const isGuest = !user?.id

  const [username, setUsername] = useState('')
  const [domain, setDomain] = useState('')
  const [domains, setDomains] = useState<string[]>([])
  const [email, setEmail] = useState('')
  const [emails, setEmails] = useState<Email[]>([])
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [tab, setTab] = useState<'inbox' | 'history'>('inbox')
  const [history, setHistory] = useState<HistoryAddress[]>([])
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Generate a random username
  const generateRandomUsername = () => {
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
    let name = ''
    for (let i = 0; i < 8; i++) name += chars[Math.floor(Math.random() * chars.length)]
    return name
  }

  // Auto-create a quick email on mount (only if user has no existing emails)
  const autoCreated = useRef(false)

  // Fetch available domains on mount
  useEffect(() => {
    fetch(`${API_BASE}/domains`)
      .then((res) => res.json())
      .then((data) => {
        setDomains(data.domains)
        if (data.domains.length > 0) {
          setDomain(data.domains[0])
          // Check history first - only auto-create if no existing emails
          if (!autoCreated.current && userId) {
            autoCreated.current = true
            fetch(`${API_BASE}/email/history/${encodeURIComponent(userId)}`)
              .then((r) => r.json())
              .then((histData) => {
                const addresses = histData.addresses || []
                if (addresses.length > 0) {
                  // Use the most recent existing email
                  const latest = addresses[0].address
                  setEmail(latest)
                  setHistory(addresses)
                  toast.info(`Loaded existing email: ${latest}`)
                } else {
                  // No history - create a quick random email
                  const randomUser = generateRandomUsername()
                  setUsername(randomUser)
                  fetch(`${API_BASE}/email/create`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ username: randomUser, domain: data.domains[0], user_id: userId }),
                  })
                    .then((r) => r.json())
                    .then((result) => {
                      if (result.email) {
                        setEmail(result.email)
                        toast.success(`Quick email ready: ${result.email}`)
                      }
                    })
                    .catch(() => {})
                }
              })
              .catch(() => {})
          }
        }
      })
      .catch(() => toast.error('Failed to connect to email API'))
  }, [userId])

  // Fetch history on mount
  const fetchHistory = useCallback(() => {
    if (!userId) return
    if (isGuest) {
      // Load history from localStorage for guest users
      const savedHistory = localStorage.getItem(`tempmail_history_${userId}`)
      const addresses = savedHistory ? JSON.parse(savedHistory) : []
      setHistory(addresses)
    } else {
      fetch(`${API_BASE}/email/history/${encodeURIComponent(userId)}`)
        .then((res) => res.json())
        .then((data) => setHistory(data.addresses || []))
        .catch(() => {})
    }
  }, [userId, isGuest])

  useEffect(() => {
    fetchHistory()
  }, [fetchHistory])

  const createEmail = () => {
    if (!username.trim()) {
      toast.error('Enter a username')
      return
    }
    if (!domain) {
      toast.error('Select a domain')
      return
    }

    if (isGuest) {
      // For guest users, save to localStorage instead of API
      const newEmail = `${username.trim()}@${domain}`
      setEmail(newEmail)
      setEmails([])
      setSelectedEmail(null)
      setTab('inbox')

      // Save to history
      const currentHistory = JSON.parse(localStorage.getItem(`tempmail_history_${userId}`) || '[]')
      const updatedHistory = [newEmail, ...currentHistory].slice(0, 20) // Keep last 20
      localStorage.setItem(`tempmail_history_${userId}`, JSON.stringify(updatedHistory))
      setHistory(updatedHistory)

      toast.success(`Email: ${newEmail}`)
      return
    }

    fetch(`${API_BASE}/email/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), domain, user_id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        setEmail(data.email)
        setEmails([])
        setSelectedEmail(null)
        setTab('inbox')
        fetchHistory()
        if (data.remaining_today !== undefined) {
          toast.success(`Email created: ${data.email} (${data.remaining_today} remaining today)`)
        } else {
          toast.success(`Email: ${data.email}`)
        }
      })
      .catch(() => toast.error('Failed to create email'))
  }

  const refreshInbox = useCallback(() => {
    if (!email) return
    setLoading(true)

    fetch(`${API_BASE}/email/${encodeURIComponent(email)}/inbox?user_id=${encodeURIComponent(userId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        const mapped: Email[] = data.emails.map((e: InboxEmail) => ({
          id: e.id,
          message_id: e.message_id,
          from: e.sender,
          sender_name: e.sender_name,
          subject: e.subject,
          body_text: '',
          body_html: '',
          date: e.received_at,
          read: false,
        }))

        setEmails((prev) => {
          const readIds = new Set(prev.filter((e) => e.read).map((e) => e.id))
          return mapped.map((e) => ({ ...e, read: readIds.has(e.id) }))
        })
      })
      .catch(() => toast.error('Failed to fetch inbox'))
      .finally(() => setLoading(false))
  }, [email, userId, isGuest])

  // Fetch inbox immediately when email changes
  useEffect(() => {
    if (email) {
      refreshInbox()
    }
  }, [email, refreshInbox, isGuest])

  // Auto-refresh every 10 seconds when enabled
  useEffect(() => {
    if (autoRefresh && email) {
      intervalRef.current = setInterval(refreshInbox, 10000)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh, email, refreshInbox])

  const openEmail = (emailItem: Email) => {
    fetch(`${API_BASE}/email/${encodeURIComponent(email)}/messages/${emailItem.id}?user_id=${encodeURIComponent(userId)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        const full: Email = {
          id: data.id,
          message_id: data.message_id,
          from: data.sender,
          sender_name: data.sender_name,
          subject: data.subject,
          body_text: data.body_text,
          body_html: data.body_html,
          date: data.received_at,
          read: true,
        }
        setSelectedEmail(full)
        setEmails((prev) =>
          prev.map((e) => (e.id === emailItem.id ? { ...e, read: true } : e))
        )
      })
      .catch(() => toast.error('Failed to load email'))
  }

  const deleteEmail = (id: number) => {
    fetch(`${API_BASE}/email/${encodeURIComponent(email)}/messages/${id}?user_id=${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          toast.error(data.error)
          return
        }
        setEmails((prev) => prev.filter((e) => e.id !== id))
        if (selectedEmail?.id === id) setSelectedEmail(null)
        toast.success('Email deleted')
      })
      .catch(() => toast.error('Failed to delete email'))
  }

  const copyEmail = async () => {
    if (!email) return
    try {
      await navigator.clipboard.writeText(email)
      setCopied(true)
      toast.success('Email copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const selectFromHistory = (address: string) => {
    setEmail(address)
    setEmails([])
    setSelectedEmail(null)
    setTab('inbox')
    toast.info(`Switched to ${address}`)
    if (isGuest) {
      // Update localStorage to move this address to top
      const currentHistory = JSON.parse(localStorage.getItem(`tempmail_history_${userId}`) || '[]')
      const updatedHistory = [address, ...currentHistory].filter(a => a !== address).slice(0, 20)
      localStorage.setItem(`tempmail_history_${userId}`, JSON.stringify(updatedHistory))
      setHistory(updatedHistory)
    }
  }

  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      month: 'short',
      day: 'numeric',
    }).format(new Date(dateStr))
  }

  // Build sandboxed HTML content for srcdoc
  const emailSrcdoc = selectedEmail?.body_html
    ? `<!DOCTYPE html><html><head><meta charset="utf-8"><style>
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 14px; line-height: 1.6; color: #333; margin: 12px; word-wrap: break-word; }
        img { max-width: 100%; height: auto; }
        a { color: #2563eb; pointer-events: none; }
        table { max-width: 100%; }
      </style></head><body>${selectedEmail.body_html}</body></html>`
    : ''

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Temporary Email</h1>
            <p className="text-muted-foreground">Disposable email address for testing</p>
            <p className="text-xs text-muted-foreground">v3.5 - Now available without login requirement</p>
          </div>
        </div>
      </div>

      {/* Create Email */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div>
              <label className="text-sm text-muted-foreground mb-2 block">Create your temporary email</label>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="username"
                  className="font-mono flex-1"
                  onKeyDown={(e) => e.key === 'Enter' && createEmail()}
                />
                <span className="hidden sm:flex items-center text-muted-foreground font-mono">@</span>
                <select
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  className="h-10 rounded-md border border-input bg-background px-3 text-sm font-mono"
                >
                  {domains.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
                <Button onClick={createEmail}>Create</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">5 emails per day limit</p>
            </div>

            {email && (
              <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                <button
                  onClick={copyEmail}
                  className="flex items-center gap-2 flex-1 w-full rounded-md border border-input bg-muted/30 px-3 py-2 text-left hover:bg-muted/60 transition-colors cursor-pointer group"
                  title="Click to copy"
                >
                  <span className="font-mono text-lg flex-1 truncate">{email}</span>
                  {copied ? (
                    <Check className="h-4 w-4 text-green-500 shrink-0" />
                  ) : (
                    <Copy className="h-4 w-4 text-muted-foreground group-hover:text-foreground shrink-0 transition-colors" />
                  )}
                </button>
                <div className="flex gap-2">
                  <div className="relative">
                    <Button onClick={refreshInbox} disabled={loading}>
                      {loading ? (
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Inbox className="h-4 w-4 mr-2" />
                      )}
                      Check Inbox
                    </Button>
                    {emails.filter((e) => !e.read).length > 0 && (
                      <span className="absolute -bottom-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-bold text-destructive-foreground">
                        {emails.filter((e) => !e.read).length}
                      </span>
                    )}
                  </div>
                  <Button
                    variant={autoRefresh ? 'default' : 'outline'}
                    onClick={() => setAutoRefresh(!autoRefresh)}
                    title="Auto-refresh every 10s"
                  >
                    <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Inbox / History */}
      <div className="flex gap-2 border-b border-border pb-0">
        <button
          onClick={() => setTab('inbox')}
          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            tab === 'inbox' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
        >
          <Inbox className="h-4 w-4 inline mr-1.5 -mt-0.5" />
          Inbox
        </button>
        {!isGuest && (
          <button
            onClick={() => { setTab('history'); fetchHistory() }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === 'history' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <History className="h-4 w-4 inline mr-1.5 -mt-0.5" />
            Email History
          </button>
        )}
      </div>

      {tab === 'history' && (
        <Card className="glass">
          <CardHeader>
            <CardTitle>Your Email Addresses</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {history.length === 0 ? (
              <div className="text-center py-12 px-4 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No email addresses created yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {history.map((item) => (
                  <div
                    key={item.address}
                    onClick={() => selectFromHistory(item.address)}
                    className={`p-4 cursor-pointer hover:bg-accent transition-colors ${
                      email === item.address ? 'bg-accent' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-sm">{item.address}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(item.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 'inbox' && (
        <div className="grid gap-6 lg:grid-cols-[350px_1fr]">
          {/* Inbox */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Inbox</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {!email ? (
                <div className="text-center py-12 px-4 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Create or select an email address to start</p>
                </div>
              ) : emails.length === 0 ? (
                <div className="text-center py-12 px-4 text-muted-foreground">
                  <Inbox className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No emails yet</p>
                  <p className="text-xs mt-1">Waiting for incoming emails...</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {emails.map((emailItem) => (
                    <div
                      key={emailItem.id}
                      onClick={() => openEmail(emailItem)}
                      className={`p-4 cursor-pointer hover:bg-accent transition-colors ${
                        selectedEmail?.id === emailItem.id ? 'bg-accent' : ''
                      } ${!emailItem.read ? 'border-l-2 border-l-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`text-sm truncate ${!emailItem.read ? 'font-semibold' : ''}`}>
                            {emailItem.sender_name || emailItem.from}
                          </p>
                          <p className="text-sm text-foreground truncate">{emailItem.subject}</p>
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(emailItem.date)}
                          </p>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 h-8 w-8"
                          onClick={(e) => {
                            e.stopPropagation()
                            deleteEmail(emailItem.id)
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Email Content */}
          <Card className="glass">
            <CardHeader>
              <CardTitle>Message</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedEmail ? (
                <div className="space-y-4">
                  <div className="space-y-2 pb-4 border-b border-border">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">From:</span>
                      <span className="font-mono text-sm">
                        {selectedEmail.sender_name ? `${selectedEmail.sender_name} <${selectedEmail.from}>` : selectedEmail.from}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Subject:</span>
                      <span className="font-medium">{selectedEmail.subject}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Date:</span>
                      <span className="text-sm">{new Date(selectedEmail.date).toLocaleString()}</span>
                    </div>
                  </div>
                  {selectedEmail.body_html ? (
                    <iframe
                      srcDoc={emailSrcdoc}
                      sandbox="allow-same-origin"
                      title="Email content"
                      className="w-full border-0 min-h-[300px] rounded bg-white"
                      style={{ height: '400px' }}
                    />
                  ) : (
                    <div className="whitespace-pre-wrap text-sm leading-relaxed">
                      {selectedEmail.body_text}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select an email to view its content</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info */}
      <Card className="glass">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 shrink-0">
              <Mail className="h-4 w-4 text-primary" />
            </div>
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-1">About Temporary Email</p>
              <p>
                Create a disposable email address to receive real emails. You can create up to 5 addresses per day.
                Once created, the address is exclusively yours. Use the History tab to access your previous addresses.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
